/**
 * 智能客服：买家新消息提示音
 * - 任意页面：店铺轮询推送新买家消息即响
 * - 声音文件打在 Electron 客户端本地（src/assets/sound/message.mp3），
 *   经 IPC 读成 data URL 播放，不请求远程服务器
 * - 总开关：localStorage kefu_notify_sound_enabled（默认开）
 */

import Call from "@/script/call";

const SOUND_ENABLED_KEY = "kefu_notify_sound_enabled";

let unlocked = false;
let pendingPlay = false;
let lastPlayAt = 0;
let unlockInstalled = false;
/** @type {string} */
let localSoundDataUrl = "";
/** @type {Promise<string> | null} */
let loadPromise = null;
/** @type {HTMLAudioElement | null} */
let sharedAudio = null;
const MIN_GAP_MS = 900;

function soundLog(step, extra) {
  try {
    const payload = Object.assign(
      {
        step: String(step || ""),
        at: Date.now(),
        unlocked: !!unlocked,
        pending: !!pendingPlay,
        enabled: isKefuNotifySoundEnabled(),
        hasSrc: !!localSoundDataUrl,
      },
      extra || {}
    );
    const line = `[sound] ${payload.step} ${JSON.stringify(payload)}`;
    if (Call && typeof Call.sendSync === "function") {
      Call.sendSync("fileEvent.log", "kefu-smart-reply.txt", line);
      return;
    }
  } catch (e) {}
  try {
    console.info("[kefu-sound]", step, extra || {});
  } catch (e2) {}
}

/** 提示音总开关（默认开启） */
export function isKefuNotifySoundEnabled() {
  try {
    if (typeof localStorage === "undefined") return true;
    const v = localStorage.getItem(SOUND_ENABLED_KEY);
    if (v == null || v === "") return true;
    return v === "1" || v === "true";
  } catch (e) {
    return true;
  }
}

export function setKefuNotifySoundEnabled(on) {
  const enabled = !!on;
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(SOUND_ENABLED_KEY, enabled ? "1" : "0");
    }
  } catch (e) {}
  if (!enabled) {
    pendingPlay = false;
    try {
      if (sharedAudio) {
        sharedAudio.pause();
        sharedAudio.currentTime = 0;
      }
    } catch (e2) {}
  }
  soundLog("setEnabled", { enabled });
  return enabled;
}

async function ensureLocalSoundUrl() {
  if (localSoundDataUrl) return localSoundDataUrl;
  if (loadPromise) return loadPromise;
  loadPromise = (async () => {
    try {
      const rs = await Call.invoke("systemEvent.getKefuNotifySoundDataUrl");
      if (rs && rs.code == 1 && rs.data) {
        localSoundDataUrl = String(rs.data);
        soundLog("loadSrc.ok", { bytes: localSoundDataUrl.length });
        return localSoundDataUrl;
      }
      soundLog("loadSrc.fail", {
        code: rs && rs.code,
        msg: (rs && rs.msg) || "empty",
      });
    } catch (e) {
      soundLog("loadSrc.err", { msg: (e && e.message) || String(e) });
    }
    return "";
  })();
  try {
    return await loadPromise;
  } finally {
    // 失败时允许下次重试
    if (!localSoundDataUrl) loadPromise = null;
  }
}

function getAudio(src) {
  if (typeof Audio === "undefined" || !src) return null;
  if (!sharedAudio) {
    sharedAudio = new Audio(src);
    sharedAudio.preload = "auto";
    sharedAudio.volume = 0.9;
  } else if (sharedAudio.src !== src && !String(sharedAudio.src).endsWith(src)) {
    sharedAudio.src = src;
  }
  return sharedAudio;
}

async function doPlay() {
  if (!isKefuNotifySoundEnabled()) {
    soundLog("play.skip", { reason: "disabled" });
    return false;
  }
  const src = await ensureLocalSoundUrl();
  if (!src) {
    soundLog("play.skip", { reason: "no_src" });
    return false;
  }
  const a = getAudio(src);
  if (!a) {
    soundLog("play.skip", { reason: "no_audio" });
    return false;
  }
  try {
    a.pause();
    a.currentTime = 0;
    const p = a.play();
    if (p && typeof p.then === "function") {
      p.then(() => {
        unlocked = true;
        soundLog("play.ok", { via: "mp3" });
      }).catch((err) => {
        pendingPlay = true;
        soundLog("play.blocked", {
          reason: "autoplay",
          msg: (err && err.message) || String(err || ""),
          name: (err && err.name) || "",
        });
      });
    } else {
      soundLog("play.ok", { sync: true, via: "mp3" });
    }
    return true;
  } catch (e) {
    pendingPlay = true;
    soundLog("play.err", { msg: (e && e.message) || String(e) });
    return false;
  }
}

/** 用户手势后解锁 */
export function unlockKefuNotifySound() {
  unlocked = true;
  void (async () => {
    if (!isKefuNotifySoundEnabled()) {
      pendingPlay = false;
      return;
    }
    const src = await ensureLocalSoundUrl();
    const a = getAudio(src);
    if (!a) {
      if (pendingPlay) {
        pendingPlay = false;
        soundLog("unlock.flushPending", { hasAudio: false });
        await doPlay();
      }
      return;
    }
    try {
      const prev = a.volume;
      a.volume = 0.01;
      const p = a.play();
      if (p && typeof p.then === "function") {
        await p;
        a.pause();
        a.currentTime = 0;
        a.volume = prev;
      } else {
        a.pause();
        a.currentTime = 0;
        a.volume = prev;
      }
      soundLog("unlock.ok", { flushPending: !!pendingPlay });
    } catch (e) {
      soundLog("unlock.err", { msg: (e && e.message) || String(e) });
    }
    if (pendingPlay) {
      pendingPlay = false;
      await doPlay();
    }
  })();
}

export function installKefuNotifySoundUnlock() {
  if (typeof document === "undefined" || unlockInstalled) return;
  unlockInstalled = true;
  const onGesture = () => {
    unlockKefuNotifySound();
  };
  document.addEventListener("pointerdown", onGesture, true);
  document.addEventListener("keydown", onGesture, true);
  // 预加载本地音源（不播）
  void ensureLocalSoundUrl();
  soundLog("unlock.install", {});
}

export function playKefuNotifySound() {
  if (!isKefuNotifySoundEnabled()) {
    pendingPlay = false;
    soundLog("play.call", { reason: "disabled" });
    return;
  }
  const now = Date.now();
  if (now - lastPlayAt < MIN_GAP_MS) {
    soundLog("play.call", { reason: "throttle", gapMs: now - lastPlayAt });
    return;
  }
  lastPlayAt = now;
  installKefuNotifySoundUnlock();
  if (!unlocked) {
    pendingPlay = true;
    soundLog("play.call", { reason: "await_unlock" });
  } else {
    soundLog("play.call", { reason: "go" });
  }
  void doPlay();
}

function normalizeMsgTimeMs(t) {
  let n = Number(t) || 0;
  if (n > 1e9 && n < 1e12) n *= 1000;
  return n;
}

/**
 * @returns {{ ding: boolean, reason: string, buyerN?: number, newN?: number, freshN?: number }}
 */
export function explainDingForMessages(state, payload) {
  if (!isKefuNotifySoundEnabled()) {
    return { ding: false, reason: "disabled" };
  }
  if (!payload || payload.type !== "messages") {
    return { ding: false, reason: "not_messages" };
  }
  const shopId = String(payload.shopId || "");
  const list = payload.messages || [];
  if (!shopId || !list.length) {
    return { ding: false, reason: "empty", shopId };
  }
  const idSet = (state && state.messageIdSet) || {};
  const cutoff = Date.now() - 5 * 60 * 1000;
  let buyerN = 0;
  let newN = 0;
  let freshN = 0;
  let sample = "";
  for (let i = 0; i < list.length; i++) {
    const m = list[i];
    if (!m || !m.id) continue;
    if (
      !(m.fromBuyer === true || m.fromBuyer === 1 || String(m.senderRole) === "1")
    ) {
      continue;
    }
    buyerN += 1;
    const mid = `${shopId}:${m.id}`;
    if (idSet[mid]) continue;
    newN += 1;
    const t = normalizeMsgTimeMs(m.createTime);
    if (t > 0 && t < cutoff) continue;
    const text = String(m.content || m.preview || "");
    if (/系统关闭会话|超时未回复/.test(text)) continue;
    freshN += 1;
    if (!sample) sample = String(text).slice(0, 24);
  }
  if (!buyerN) return { ding: false, reason: "no_buyer", shopId, buyerN, newN, freshN };
  if (!newN) return { ding: false, reason: "dup", shopId, buyerN, newN, freshN };
  if (!freshN) {
    // 对账灌历史通常几百条；≤8 条更像实时增量，时间戳异常时仍响
    if (list.length <= 8) {
      return {
        ding: true,
        reason: "ok_small_batch",
        shopId,
        buyerN,
        newN,
        freshN,
        sample,
      };
    }
    return { ding: false, reason: "stale_or_system", shopId, buyerN, newN, freshN };
  }
  return { ding: true, reason: "ok", shopId, buyerN, newN, freshN, sample };
}

export function shouldDingForMessages(state, payload) {
  const ex = explainDingForMessages(state, payload);
  if (!ex.ding) {
    // 空推/非买家太多时不刷日志；有买家但不响时记下原因
    if (ex.reason !== "empty" && ex.reason !== "not_messages" && ex.reason !== "no_buyer") {
      soundLog("ding.skip", ex);
    } else if (ex.reason === "no_buyer" && (payload.messages || []).length) {
      soundLog("ding.skip", ex);
    }
    return false;
  }
  soundLog("ding.yes", ex);
  return true;
}
